// Render the worksheet card into a PDF that matches the Print view and emails
// cleanly: we build our OWN off-screen clone (so we can bake live input values
// in as static text and control pagination), then capture it block-by-block so
// page breaks land between items instead of slicing through a diagram.
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const A4 = { w: 595.28, h: 841.89 }; // points
const MARGIN = 28;
const RENDER_W = 760; // px width of the off-screen render host
// Unique id for the off-screen render host. A <style> element applies to the
// WHOLE document regardless of where it sits in the DOM, so every print-capture
// rule below is namespaced under this id — that way the high-fidelity print
// tweaks can NEVER bleed back into the live on-screen worksheet preview.
const HOST_ID = "__ws_pdf_capture_host";

// html2canvas can emit a canvas with transparent regions; encoding that straight
// to JPEG turns the transparency BLACK (JPEG has no alpha), which showed up as a
// black bar at page breaks. Compositing onto an opaque white canvas first
// guarantees a clean white background.
function _flattenToJpeg(canvas) {
  const out = document.createElement("canvas");
  out.width = canvas.width;
  out.height = canvas.height;
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, 0);
  return out.toDataURL("image/jpeg", 0.92);
}

// Print-capture CSS — applied ONLY inside the off-screen host (every selector is
// scoped under #HOST_ID) so these high-fidelity tweaks stay isolated to the PDF
// layer and never touch the live viewport: give headings vertical breathing room
// so html2canvas doesn't clip ascenders/descenders, and let content overflow
// visibly during capture.
const _CAPTURE_CSS =
  `#${HOST_ID} h1,#${HOST_ID} h2,#${HOST_ID} h3,#${HOST_ID} h4` +
  `{line-height:1.35;padding-top:3px;padding-bottom:2px;overflow:visible;}` +
  `#${HOST_ID} *{overflow:visible;}`;

// Replace a live <input>/<textarea>/radio in the clone with static markup that
// shows its value (read from the original control `src`, since cloneNode drops
// live values). Returns nothing; mutates the clone in place.
function _replaceControl(cloneEl, src) {
  const doc = cloneEl.ownerDocument;
  const type = (cloneEl.getAttribute("type") || "").toLowerCase();
  if (type === "radio") {
    const checked = src ? src.checked : cloneEl.checked;
    const dot = doc.createElement("span");
    dot.style.cssText =
      "display:inline-block;flex:0 0 auto;align-self:center;width:13px;height:13px;" +
      "box-sizing:border-box;border:2px solid #0A0A0A;border-radius:50%;margin-right:6px;" +
      "vertical-align:middle;" +
      (checked ? "background:#0A0A0A;" : "background:#fff;");
    cloneEl.replaceWith(dot);
    return;
  }
  const val = src ? (src.value || "") : (cloneEl.value || "");
  const cs = window.getComputedStyle(src || cloneEl);
  const isMeta = (cloneEl.getAttribute("data-testid") || "").startsWith("meta-");
  const box = doc.createElement("div");
  box.textContent = val;
  box.style.fontSize = cs.fontSize;
  box.style.fontFamily = cs.fontFamily;
  box.style.fontWeight = cs.fontWeight;
  box.style.color = "#0A0A0A";
  box.style.whiteSpace = "pre-wrap";
  box.style.wordBreak = "break-word";
  if (isMeta) {
    box.style.display = "inline-block";
    box.style.minWidth = "8rem";
    box.style.borderBottom = "2px solid rgba(10,10,10,0.55)";
    box.style.lineHeight = "1.7";
    // Lift the typed value well clear of its own underline so the baseline rule
    // never clips the text's descenders (Name/Grade values sat right on the line).
    box.style.padding = "0 2px 5px";
    box.style.transform = "translateY(-4px)";
  } else {
    box.style.border = "2px solid #0A0A0A";
    box.style.borderRadius = "8px";
    box.style.padding = "8px 12px";
    box.style.minHeight = "1.5em";
    box.style.maxWidth = "24rem";
  }
  cloneEl.replaceWith(box);
}

// Center each per-problem visual within the print clone WITHOUT widening its
// bordered card: drop a full-width flex wrapper around the card and move the card
// into it. Runs on the clone only, so the on-screen preview keeps its original
// left-aligned flow.
function _centerVisuals(clone) {
  clone.querySelectorAll('[data-testid^="problem-visual"]').forEach((fig) => {
    const card = fig.parentElement; // ProblemVisual's bordered inline-flex wrapper
    if (!card || !card.parentElement) return;
    const center = clone.ownerDocument.createElement("div");
    center.style.cssText = "display:flex;justify-content:center;width:100%;";
    card.parentElement.insertBefore(center, card);
    center.appendChild(card);
  });
}

// Vertical breathing room (px, pre-scale) added around every captured block so
// html2canvas never clips the top of a heading / number bubble or the descenders
// of italic instruction text at the block's edge.
const BLOCK_PAD = 10;

// Capture one block. We slot it into a padded wrapper first: html2canvas renders
// exactly the element's box and shaves anything that overflows that box (tall
// ascenders, rounded borders, descenders), so the padding guarantees a clean
// margin on all sides. The wrapper is full-width with horizontal padding 0, so
// the block's own width/layout is untouched.
async function _captureBlock(el) {
  const doc = el.ownerDocument;
  const parent = el.parentNode;
  const next = el.nextSibling;
  const pad = doc.createElement("div");
  // Small horizontal pad too, so a control whose border sits at the block's left
  // edge (e.g. the question-number bubble) isn't shaved by html2canvas.
  pad.style.cssText = `padding:${BLOCK_PAD}px 5px;background:#ffffff;box-sizing:border-box;`;
  if (parent) parent.insertBefore(pad, next);
  pad.appendChild(el);
  try {
    return await html2canvas(pad, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
  } finally {
    // Leave the clone tree as we found it (harmless either way — it's discarded).
    if (parent) parent.insertBefore(el, pad);
    pad.remove();
  }
}

// Collect the elements to place as atomic blocks (never split across a page):
// the header pieces (title / instructions / meta), then each section heading and
// each item <li>. Each block carries `keepWithNext` — true for a "Part N." section
// heading, so it can't be stranded alone at the bottom of a page.
function _collectBlocks(clone) {
  const blocks = [];
  const kids = Array.from(clone.children);
  const sectionsContainer = kids.find((k) => k.querySelector && k.querySelector("section"));
  for (const k of kids) {
    if (k === sectionsContainer) {
      k.querySelectorAll(":scope > section").forEach((sec) => {
        Array.from(sec.children).forEach((sk) => {
          if (sk.tagName === "OL") {
            sk.querySelectorAll(":scope > li").forEach((li) => blocks.push({ el: li, keepWithNext: false }));
          } else {
            blocks.push({ el: sk, keepWithNext: true }); // the "Part N." heading row
          }
        });
      });
    } else if (Array.from(k.children).some((c) => c.classList.contains("break-inside-avoid"))) {
      // A flatter wrapper (no <section>/<ol>, e.g. Practice's print layout) whose
      // direct children are already individually marked atomic — expand them
      // instead of capturing the whole wrapper as one oversized block, which would
      // otherwise get sliced across pages mid-question.
      Array.from(k.children).forEach((c) => blocks.push({ el: c, keepWithNext: false }));
    } else {
      blocks.push({ el: k, keepWithNext: false });
    }
  }
  return blocks;
}

export async function buildWorksheetPdfBase64(node) {
  const usableW = A4.w - MARGIN * 2;
  const usableH = A4.h - MARGIN * 2;

  // 1) Off-screen host with a print-styled clone of the worksheet card. The host
  //    carries HOST_ID so the scoped capture CSS only ever applies in here.
  const host = document.createElement("div");
  host.id = HOST_ID;
  host.style.cssText = `position:fixed;left:-100000px;top:0;width:${RENDER_W}px;background:#fff;`;
  const clone = node.cloneNode(true);
  Object.assign(clone.style, {
    border: "0", boxShadow: "none", borderRadius: "0", margin: "0", padding: "0",
    width: "100%", maxWidth: "none", background: "#fff",
  });
  // Inject capture-only CSS (scoped under #HOST_ID) so headings get vertical
  // breathing room without ever affecting the live preview.
  const style = document.createElement("style");
  style.textContent = _CAPTURE_CSS;
  host.appendChild(style);
  host.appendChild(clone);
  document.body.appendChild(host);

  try {
    // Drop buttons/symbol rows and the revealed answer key.
    clone.querySelectorAll(".no-print, [data-no-email]").forEach((e) => e.remove());
    // Bake live values into static markup (cloneNode doesn't copy them).
    const liveControls = node.querySelectorAll("input, textarea");
    const cloneControls = clone.querySelectorAll("input, textarea");
    cloneControls.forEach((el, i) => _replaceControl(el, liveControls[i]));
    // Center per-problem visuals in the PRINT clone only (kept left-aligned in the
    // live viewport): wrap each visual card in a full-width centering div here.
    _centerVisuals(clone);
    // Lift static underlined values (e.g. the Score) clear of their rule so the
    // baseline rule never clips the digits — matches the baked meta fields.
    clone.querySelectorAll("[data-print-lift]").forEach((el) => {
      el.style.paddingBottom = "5px";
    });

    // Rasterize every block up front so pagination can look ahead: a section
    // heading must land on the same page as its first item.
    const rendered = [];
    for (const { el, keepWithNext } of _collectBlocks(clone)) {
      const canvas = await _captureBlock(el);
      if (!canvas.width || !canvas.height) continue;
      rendered.push({
        imgData: _flattenToJpeg(canvas),
        imgH: canvas.height * (usableW / canvas.width),
        keepWithNext,
      });
    }

    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageBottom = MARGIN + usableH;
    let y = MARGIN;

    for (let i = 0; i < rendered.length; i++) {
      const { imgData, imgH, keepWithNext } = rendered[i];

      if (imgH > usableH) {
        // Block taller than a page → start fresh and slice it across pages.
        if (y > MARGIN) { pdf.addPage(); y = MARGIN; }
        let off = 0;
        while (off < imgH) {
          pdf.addImage(imgData, "JPEG", MARGIN, MARGIN - off, usableW, imgH);
          off += usableH;
          if (off < imgH) pdf.addPage();
        }
        // Continue on the last slice's page (no stray blank page).
        y = MARGIN + (imgH - (off - usableH)) + 6;
        continue;
      }

      // Keep a heading with its first item: reserve room for this block PLUS the
      // next one (when the next fits on a page). If the pair won't fit in what's
      // left, break to a fresh page first so they travel together.
      let needed = imgH;
      const next = rendered[i + 1];
      if (keepWithNext && next && next.imgH <= usableH) needed += 6 + next.imgH;
      if (y > MARGIN && y + needed > pageBottom) { pdf.addPage(); y = MARGIN; }

      pdf.addImage(imgData, "JPEG", MARGIN, y, usableW, imgH);
      y += imgH + 6;
    }

    return pdf.output("datauristring").split(",", 2)[1];
  } finally {
    document.body.removeChild(host);
  }
}

// Decode a base64 PDF into an object URL for viewing/printing in the browser.
function _pdfBase64ToBlobUrl(b64) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
}

// Build the worksheet PDF and return an object URL for it (or null on failure).
// Lets callers show an in-page preview instead of forcing a download/new tab.
export async function buildWorksheetPdfBlobUrl(node) {
  const b64 = await buildWorksheetPdfBase64(node);
  return b64 ? _pdfBase64ToBlobUrl(b64) : null;
}

// Build the worksheet PDF with the SAME generator the teacher email uses, then
// open it in a new tab (falling back to a download if a popup is blocked). This
// guarantees what the Print button produces is byte-for-byte the file the
// teacher receives. Returns true on success.
export async function openWorksheetPdf(node, { filename = "worksheet.pdf" } = {}) {
  const b64 = await buildWorksheetPdfBase64(node);
  if (!b64) return false;
  const url = _pdfBase64ToBlobUrl(b64);
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Revoke later so the new tab/download has time to read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
}
